import React, { useEffect, useState } from "react";
import {
  FormControl,
  InputLabel,
  Input,
  FormGroup,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Button,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import stylesDefault from "../styles";
import { InputLengthHelperText } from '../../Common/FormModal';
import { LocationDTO } from "@beable/roster-backend-client-v1";

interface LocationFormProps {
  parentLocations: LocationDTO[];
  submitForm: (name: string, isoCode: string, parentId: number, id?: number, hasChildren?: boolean) => void;
  onClose: () => void;
  selectedItem?: LocationDTO;
}

export default function LocationForm({
  parentLocations,
  submitForm,
  onClose,
  selectedItem,
}: LocationFormProps) {
  const theme = useTheme();
  const styles = stylesDefault.addModalStyles(theme);

  const [id, setId] = useState<number | undefined>();
  const [name, setName] = useState("");
  const [isoCode, setIsoCode] = useState("");
  const [hasParent, setHasParent] = useState(false);
  const [parentId, setParentId] = useState<number | undefined>();
  const [hasChildren, setHasChildren] = useState(false);

  const getParentSelectValue = () => {
    if (!hasParent || !parentId || parentId <= 0) {
      return '';
    }

    return parentLocations.some((pl) => pl.id === parentId)
      ? parentId.toString()
      : '';
  };

  const handleSubmit = () => {
    submitForm(name, isoCode, ((hasParent && parentId) ? parentId : 0), id, hasChildren);
  }

  const resetState = () => {
    setId(undefined);
    setName("");
    setIsoCode("");
    setParentId(undefined);
    setHasParent(false);
    setHasChildren(false);
  }

  const handleClose = () => {
    resetState();
    onClose();
  }

  useEffect(() => {
    const initializeForm = () => {
      setId(selectedItem!.id);
      setName(selectedItem!.name);
      setIsoCode(selectedItem!.isoCode);
      setParentId(selectedItem!.parentId || undefined);
      setHasParent(!!selectedItem!.parentId);
      setHasChildren(selectedItem!.hasChildren ? selectedItem!.hasChildren : false);
    }

    if (selectedItem) {
      initializeForm();
    }
  }, [selectedItem]);

  return <>
    <FormGroup>
      <FormControl variant="standard">
        <InputLabel htmlFor="name">Name*</InputLabel>
        <Input
          onChange={(e: any) => setName(e.target.value)}
          value={name}
          id="name"
          aria-describedby="location name"
        />
        <InputLengthHelperText str={name} />
      </FormControl>
      <FormControl variant="standard">
        <InputLabel htmlFor="isoCode">ISO Code*</InputLabel>
        <Input
          onChange={(e: any) => setIsoCode(e.target.value)}
          value={isoCode}
          id="isoCode"
          aria-describedby="location isoCode"
        />
        <InputLengthHelperText str={isoCode} maxLength={7} />
      </FormControl>
      <FormControlLabel
        control={
          <Checkbox
            sx={styles.cbs}
            checked={hasParent}
            onChange={() => setHasParent((prevState) => !prevState)}
          />
        }
        label="Has parent"
      />
      <FormControl
        variant="standard"
        sx={hasParent ? styles.dflex : styles.dnone}>
        <InputLabel htmlFor="parentIdPicker">Parent Picker</InputLabel>
        <Select
          variant="standard"
          labelId="parentIdPicker"
          id="parentIdPicker"
          value={getParentSelectValue()}
          onChange={(e: any) => setParentId(Number(e.target.value))}>
          {parentLocations.map((pl) => {
            if (pl.id == null) {
              return null;
            }

            return (
              <MenuItem value={pl.id.toString()} key={pl.id}>
                {pl.name}
              </MenuItem>
            );
          })}
        </Select>
      </FormControl>
    </FormGroup>
    <FormGroup sx={[styles.mt2, styles.dfr]}>
      <Button
        variant="contained"
        color="primary"
        disabled={!name || !isoCode}
        onClick={handleSubmit}
      >
        {selectedItem ? 'Edit' : 'Add location'}
      </Button>
      <Button variant="contained" onClick={handleClose} sx={styles.ml2}>
        Close
      </Button>
    </FormGroup>
  </>;
}